import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { getXPSummary } from './xpService';
import { getMultipleTeacherRatings } from './bookingService';
import { getHybridRecommendations } from './recommendationService';
import { FaSearch, FaBell, FaChevronLeft, FaChevronRight, FaClock, FaArrowUp, FaEdit, FaBook, FaMapMarkerAlt, FaStar, FaFileAlt, FaGlobe, FaUser, FaComments } from 'react-icons/fa';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameMonth, isToday, parseISO } from 'date-fns';
import { toArabicNumerals, formatNumber, formatMonthYear, formatFullDate, translateSubject } from './utils/arabicNumbers';
import { createMapsUrl } from './components/location/locationUtils';
import logger from './utils/logger';

// Brand colors
const COLORS = {
  light: '#FFF8F1',
  dark: '#1A2341',
  orange: '#FF7A1A',
  orangeLight: '#f3b68aff',
  blue: '#22305A',
  white: '#FFFFFF',
  gray: '#F5F7FA',
  textSecondary: '#64748B',
  borderLight: '#E2E8F0'
};

// Default avatar placeholder (simple gradient circle)
const DEFAULT_AVATAR = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23f3b68a" width="100" height="100"/%3E%3Ctext x="50" y="50" font-size="40" text-anchor="middle" dy=".3em" fill="%23fff" font-family="Arial"%3E%F0%9F%91%A4%3C/text%3E%3C/svg%3E';

export default function StudentDashboard() {
  const navigate = useNavigate();
  const { isArabic, cachedUserProfile } = useOutletContext();

  // Responsive hook
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth > 768 && window.innerWidth <= 1024);

  // Dashboard data state
  const [dashboardData, setDashboardData] = useState({
    upcomingSession: null,
    stats: { totalSessions: 0, hoursLearned: { hours: 0, minutes: 0 } },
    xpData: { total_xp: 0, current_level: 1, xp_to_next_level: 50 },
    recommendedTeachers: [],
    onboardingCompleted: true, // Track if student completed onboarding
    recentMessages: [],
    scheduledDates: [],
    todaySessions: [],
    loading: true,
    error: null
  });

  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateSessions, setSelectedDateSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth > 768 && window.innerWidth <= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return isArabic ? 'صباح الخير' : 'Good morning';
    if (hour < 18) return isArabic ? 'مساء الخير' : 'Good afternoon';
    return isArabic ? 'مساء الخير' : 'Good evening';
  };

  // Fetch all dashboard data in parallel
  useEffect(() => {
    async function fetchDashboardData() {
      try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
          setDashboardData(prev => ({ ...prev, loading: false }));
          return;
        }

        // Parallel fetch using Promise.allSettled for resilience
        const [
          upcomingSessionResult,
          statsResult,
          xpResult,
          recommendedResult,
          messagesResult,
          bookingsResult
        ] = await Promise.allSettled([
          fetchUpcomingSession(user.id),
          fetchSessionStats(user.id),
          getXPSummary(user.id),
          fetchRecommendedTeachers(user.id),
          fetchRecentMessages(user.id),
          fetchMonthBookings(user.id, currentMonth)
        ]);

        // Extract recommendations and onboarding status
        const recommendationData = recommendedResult.status === 'fulfilled'
          ? recommendedResult.value
          : { recommendations: [], onboardingCompleted: false };

        setDashboardData({
          upcomingSession: upcomingSessionResult.status === 'fulfilled' ? upcomingSessionResult.value : null,
          stats: statsResult.status === 'fulfilled' ? statsResult.value : { totalSessions: 0, hoursLearned: { hours: 0, minutes: 0 } },
          xpData: xpResult.status === 'fulfilled' && xpResult.value?.data
            ? xpResult.value.data
            : { total_xp: 0, current_level: 1, xp_to_next_level: 50 },
          recommendedTeachers: recommendationData.recommendations,
          onboardingCompleted: recommendationData.onboardingCompleted,
          recentMessages: messagesResult.status === 'fulfilled' ? messagesResult.value : [],
          scheduledDates: bookingsResult.status === 'fulfilled' ? bookingsResult.value.dates : [],
          todaySessions: bookingsResult.status === 'fulfilled' ? bookingsResult.value.todaySessions : [],
          loading: false,
          error: null
        });
      } catch (error) {
        logger.error('Error fetching dashboard data:', error);
        setDashboardData(prev => ({ ...prev, loading: false, error: error.message }));
      }
    }

    fetchDashboardData();
  }, [currentMonth]);

  // Individual fetch functions
  async function fetchUpcomingSession(userId) {
      const today = new Date().toISOString().split('T')[0];

    const { data, error } = await supabase
        .from('booking_requests')
      .select('*')
      .eq('student_id', userId)
        .gte('date', today)
        .eq('status', 'accepted')
        .eq('payment_status', 'paid')
        .order('date', { ascending: true })
        .order('time_from', { ascending: true })
        .limit(1)
      .maybeSingle();

    if (error) {
      return null;
    }

    // If we have a session, fetch the teacher's profile photo
    if (data && data.teacher_email) {
      const { data: teacherData, error: teacherError } = await supabase
        .from('Teachers')
        .select('profile_photo')
        .eq('email', data.teacher_email)
        .single();

      if (!teacherError && teacherData) {
        data.teacher_profile_image = teacherData.profile_photo;
      }
    }

    return data;
  }

  async function fetchSessionStats(userId) {
    // Get all sessions first
    const { data: allSessions, error: sessionsError } = await supabase
        .from('booking_requests')
      .select('time_from, time_to, status')
      .eq('student_id', userId);

    // Filter out rejected/cancelled and count
    const validSessions = allSessions?.filter(s => s.status !== 'rejected' && s.status !== 'cancelled') || [];
    const totalSessions = validSessions.length;

    // Calculate total hours learned by parsing time_from and time_to
    let totalHours = 0;
    if (validSessions.length > 0) {
      totalHours = validSessions.reduce((sum, session) => {
        if (!session.time_from || !session.time_to) return sum;

        // Parse times like "2:00 PM" or "14:00"
        const parseTime = (timeStr) => {
          const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)?/i);
          if (!match) return 0;

          let hours = parseInt(match[1]);
          const minutes = parseInt(match[2]);
          const isPM = match[3]?.toUpperCase() === 'PM';

          if (isPM && hours !== 12) hours += 12;
          if (!isPM && hours === 12) hours = 0;

          return hours + minutes / 60;
        };

        const startHours = parseTime(session.time_from);
        const endHours = parseTime(session.time_to);
        const duration = endHours - startHours;

        return sum + (duration > 0 ? duration : 0);
      }, 0);
    }

    const hours = Math.floor(totalHours);
    const minutes = Math.round((totalHours - hours) * 60);


    return {
      totalSessions: totalSessions || 0,
      hoursLearned: { hours, minutes }
    };
  }

  async function fetchRecommendedTeachers(userId) {
    try {
      // Use the new hybrid recommendation service that combines preference-based and history-based matching
      const result = await getHybridRecommendations(userId, 4);

      return {
        recommendations: result.recommendations || [],
        onboardingCompleted: result.onboardingCompleted
      };
    } catch (error) {
      logger.error('Error in fetchRecommendedTeachers:', error);
      return {
        recommendations: [],
        onboardingCompleted: false
      };
    }
  }

  async function fetchRecentMessages(userId) {
    // First get conversations where this student is a participant
    const { data: conversations } = await supabase
      .from('conversations')
      .select('id, user1_id, user2_id')
      .or(`user1_id.eq.${userId},user2_id.eq.${userId}`)
      .order('created_at', { ascending: false })
      .limit(5);

    if (!conversations || conversations.length === 0) return [];

    // Get the most recent message from EACH conversation (not 2 total across all)
    const enrichedMessages = await Promise.all(conversations.map(async (conversation) => {
      // Get the most recent message for THIS specific conversation
      const { data: message } = await supabase
        .from('messages')
        .select('id, content, created_at, sender_id, conversation_id')
        .eq('conversation_id', conversation.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!message) return null;

      // Determine the teacher ID from the conversation participants
      const teacherId = conversation.user1_id === userId ? conversation.user2_id : conversation.user1_id;

      // Fetch teacher info directly from Teachers table using uuid_id (auth user ID)
      const { data: teacher } = await supabase
        .from('Teachers')
        .select('full_name, profile_photo, email')
        .eq('uuid_id', teacherId)
        .maybeSingle();

      return {
        ...message,
        teacher_name: teacher?.full_name || 'Teacher',
        teacher_profile_image: teacher?.profile_photo || null,
        teacher_email: teacher?.email || null
      };
    }));

    // Filter out null values (conversations with no messages)
    return enrichedMessages.filter(msg => msg !== null);
  }

  async function fetchMonthBookings(userId, month) {
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    const today = format(new Date(), 'yyyy-MM-dd');

    const { data: bookings, error } = await supabase
      .from('booking_requests')
      .select('*')
      .eq('student_id', userId)
      .gte('date', format(monthStart, 'yyyy-MM-dd'))
      .lte('date', format(monthEnd, 'yyyy-MM-dd'))
      .order('date', { ascending: true });

    if (error) {
      logger.error('Error fetching month bookings:', error);
      logger.error('Error details:', error);
    }


    if (!bookings) return { dates: [], todaySessions: [] };

    // Filter for confirmed sessions only (accepted AND paid)
    const confirmedBookings = bookings.filter(b => b.status === 'accepted' && b.payment_status === 'paid');

    const dates = [...new Set(confirmedBookings.map(b => b.date))];
    const todaySessions = confirmedBookings.filter(b => b.date === today);

    console.log('[Dashboard] Scheduled dates:', dates);
    console.log('[Dashboard] Today\'s sessions:', todaySessions);

    return { dates, todaySessions };
  }

  // Calendar navigation
  const handlePrevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  // Handle date click to show sessions
  const handleDateClick = async (dateStr, day) => {
    const isScheduled = dashboardData.scheduledDates.includes(dateStr);

    if (!isScheduled) {
      setSelectedDate(null);
      setSelectedDateSessions([]);
      return;
    }

    // If clicking the same date, toggle it off
    if (selectedDate === dateStr) {
      setSelectedDate(null);
      setSelectedDateSessions([]);
      return;
    }

    // Show loading state
    setLoadingSessions(true);
    setSelectedDate(dateStr); // Set date immediately for visual feedback
    setSelectedDateSessions([]); // Clear previous sessions

    // Fetch sessions for this date
    const { data: { user } } = await supabase.auth.getUser();

    if (!user?.id) {
      setLoadingSessions(false);
      return;
    }

    const { data: sessions, error } = await supabase
        .from('booking_requests')
      .select('*')
      .eq('student_id', user.id)
        .eq('date', dateStr)
      .eq('status', 'accepted')
      .eq('payment_status', 'paid')
      .order('time_from', { ascending: true });

    setLoadingSessions(false);

    if (error) {
      setSelectedDateSessions([]);
    } else {
      // Set sessions after fetching
      setSelectedDateSessions(sessions || []);
    }
  };

  // Render calendar - One week view starting from today
  const renderCalendar = () => {
    const today = new Date();
    const startDate = startOfWeek(today, { weekStartsOn: 0 }); // Start from Sunday
    const days = [];

    for (let i = 0; i < 7; i++) {
      const day = addDays(startDate, i);
      const formattedDate = format(day, 'd');
      const dateStr = format(day, 'yyyy-MM-dd');
      const isScheduled = dashboardData.scheduledDates.includes(dateStr);
      const isTodayDate = isToday(day);
      const isSelected = selectedDate === dateStr;

      // Determine background and text color based on state priority
      let bgColor, textColor, borderStyle;
      if (isTodayDate) {
        // Today is always solid orange
        bgColor = COLORS.orange;
        textColor = COLORS.white;
        borderStyle = 'none';
      } else if (isSelected) {
        // Selected date has orange border with light background
        bgColor = '#fff9f0';
        textColor = COLORS.orange;
        borderStyle = `2px solid ${COLORS.orange}`;
      } else if (isScheduled) {
        // Scheduled dates have light background
        bgColor = COLORS.light;
        textColor = COLORS.dark;
        borderStyle = 'none';
      } else {
        // Default state
        bgColor = 'transparent';
        textColor = COLORS.dark;
        borderStyle = 'none';
      }

      days.push(
        <div
          key={day.toString()}
          onClick={() => handleDateClick(dateStr, day)}
          style={{
            padding: isMobile ? '12px 8px' : '16px 12px',
            textAlign: 'center',
            cursor: isScheduled ? 'pointer' : 'default',
            color: textColor,
            fontWeight: (isTodayDate || isSelected) ? 700 : isScheduled ? 600 : 400,
            backgroundColor: bgColor,
            borderRadius: '12px',
            position: 'relative',
            transition: 'all 0.2s ease',
            fontSize: isMobile ? '14px' : '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            border: borderStyle
          }}
          onMouseEnter={(e) => {
            if (!isTodayDate && !isSelected && isScheduled) {
              e.currentTarget.style.backgroundColor = '#ffe8d6';
            }
          }}
          onMouseLeave={(e) => {
            if (!isTodayDate && !isSelected) {
              if (!isScheduled) {
                e.currentTarget.style.backgroundColor = 'transparent';
              } else {
                e.currentTarget.style.backgroundColor = COLORS.light;
              }
            } else if (isSelected && !isTodayDate) {
              e.currentTarget.style.backgroundColor = '#fff9f0';
            }
          }}
        >
          {toArabicNumerals(formattedDate, isArabic)}
          {isScheduled && !isTodayDate && !isSelected && (
            <div style={{
              width: '4px',
              height: '4px',
              borderRadius: '50%',
              backgroundColor: COLORS.orange,
              margin: '0 auto'
            }} />
          )}
        </div>
      );
  }

  return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: isMobile ? '6px' : '8px'
      }}>
        {days}
      </div>
    );
  };

  // Loading state
  if (dashboardData.loading) {
    return (
      <div style={{
        backgroundColor: 'transparent',
          minHeight: '100vh',
        padding: isMobile ? '16px' : '32px',
                display: 'flex',
                alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: `4px solid ${COLORS.borderLight}`,
            borderTop: `4px solid ${COLORS.orange}`,
                    borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
            {isArabic ? 'جاري التحميل...' : 'Loading your dashboard...'}
                  </p>
                </div>
              </div>
    );
  }

  const studentName = cachedUserProfile?.full_name || (isArabic ? 'الطالب' : 'Student');

  return (
    <div style={{
      backgroundColor: '#f7f9fb',
      minHeight: '100vh',
      padding: isMobile ? '16px' : isTablet ? '24px' : '32px'
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      {/* Header */}
      <div style={{
                display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
        marginTop: isMobile ? '16px' : '0',
        marginBottom: isMobile ? '24px' : '32px',
        gap: isMobile ? '16px' : '0'
      }}>
        {/* Greeting */}
        <div>
          <h1 style={{
            fontSize: isMobile ? '28px' : '32px',
            fontWeight: 700,
            color: '#1E293B',
            margin: 0,
            marginBottom: '8px'
          }}>
            {getGreeting()}, {studentName}
          </h1>
          <p style={{
            fontSize: '14px',
            color: '#64748B',
            margin: 0
          }}>
            {isArabic ? 'مستعد لمتابعة التعلم اليوم؟' : 'Ready to continue learning today?'}
          </p>
              </div>

        {/* Search & Notifications */}
        {!isMobile && (
          <div style={{
                  display: 'flex',
                  alignItems: 'center',
            gap: '16px'
          }}>
            <div style={{ position: 'relative', width: '400px' }}>
                  <input
                    type="text"
                placeholder={isArabic ? 'البحث عن دورات، مدرسين، أو مواضيع...' : 'Search courses, tutors, or topics...'}
                    style={{
                  width: '100%',
                  padding: '12px 16px 12px 40px',
                  borderRadius: '12px',
                  border: `1px solid ${COLORS.borderLight}`,
                  fontSize: '14px',
                      outline: 'none',
                  backgroundColor: COLORS.white
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && e.target.value) {
                    navigate(`/dashboard/student/teachers?search=${e.target.value}`);
                  }
                }}
              />
              <FaSearch style={{
                        position: 'absolute',
                left: '14px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                color: COLORS.textSecondary,
                fontSize: '16px'
              }} />
                </div>
            <button style={{
              padding: '12px',
              borderRadius: '12px',
                    border: 'none',
              backgroundColor: COLORS.white,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
            }}>
              <FaBell style={{ fontSize: '18px', color: COLORS.dark }} />
                </button>
            </div>
          )}
      </div>

      {/* Main Dashboard Grid */}
          <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile || isTablet ? '1fr' : '2fr 1fr',
        gap: isMobile ? '16px' : '24px',
        marginBottom: '80px'
      }}>
        {/* Left Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '16px' : '24px' }}>
          {/* Stats Row */}
            <div style={{
              display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '16px'
          }}>
            {/* XP Card */}
            <div style={{
              background: `linear-gradient(135deg, ${COLORS.orange} 0%, ${COLORS.orangeLight} 100%)`,
              borderRadius: '16px',
              padding: isMobile ? '20px' : '24px',
              color: COLORS.white,
                position: 'relative',
                overflow: 'hidden',
              boxShadow: '0 4px 16px rgba(255, 122, 26, 0.2)',
              cursor: 'pointer',
              transition: 'transform 0.2s ease'
            }}
            onClick={() => navigate('/dashboard/student/xp')}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <div style={{
                position: 'absolute',
                top: '-10px',
                right: '-10px',
                fontSize: '80px',
                opacity: 0.15
              }}>
                ⚡
              </div>
                <div style={{ position: 'relative', zIndex: 1 }}>
                {dashboardData.upcomingSession && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px',
                    fontSize: '11px',
                    fontWeight: 600,
                    opacity: 0.95,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    <div style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      backgroundColor: COLORS.white
                    }} />
                    {isArabic ? 'قادم' : 'UP NEXT'}
                  </div>
                )}
                <h2 style={{
                  fontSize: isMobile ? '42px' : '48px',
                  fontWeight: 800,
                  margin: '8px 0 20px 0',
                  lineHeight: 1
                }}>
                  {toArabicNumerals(dashboardData.xpData.total_xp, isArabic)}
                  <span style={{ fontSize: '18px', opacity: 0.9, marginLeft: '4px' }}>XP</span>
                </h2>
                <div style={{
                  width: '100%',
                  height: '8px',
                  backgroundColor: 'rgba(255,255,255,0.3)',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  marginBottom: '8px'
                }}>
                  <div style={{
                    width: `${Math.max(0, Math.min(100, ((50 - dashboardData.xpData.xp_to_next_level) / 50) * 100))}%`,
                    height: '100%',
                    backgroundColor: COLORS.white,
                    borderRadius: '4px',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <p style={{
                  fontSize: '12px',
                  margin: 0,
                  opacity: 0.9
                }}>
                  {toArabicNumerals(dashboardData.xpData.xp_to_next_level, isArabic)} XP {isArabic ? 'للمستوى التالي' : 'to next level'}
                </p>
                      <button
                        style={{
                    marginTop: '16px',
                    padding: '10px 20px',
                    border: '2px solid rgba(255,255,255,0.5)',
                    borderRadius: '8px',
                    backgroundColor: 'transparent',
                    color: COLORS.white,
                          fontWeight: 700,
                    fontSize: '14px',
                          cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.stopPropagation();
                    e.target.style.backgroundColor = 'rgba(255,255,255,0.2)';
                    e.target.style.borderColor = 'rgba(255,255,255,0.8)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.borderColor = 'rgba(255,255,255,0.5)';
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/dashboard/student/xp');
                  }}
                >
                  {isArabic ? 'عرض التاريخ ←' : 'View History →'}
                      </button>
              </div>
            </div>

            {/* Total Learning Card */}
            <div style={{
              backgroundColor: COLORS.white,
              borderRadius: '16px',
              padding: isMobile ? '20px' : '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                          display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              transition: 'transform 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <div>
                <div style={{
                        display: 'flex',
                        alignItems: 'center',
                  gap: '8px',
                  marginBottom: '16px'
                }}>
                  <span style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>
                    {isArabic ? 'إجمالي التعلم' : 'Total Learning'}
                  </span>
                  <FaArrowUp style={{ fontSize: '12px', color: '#10B981' }} />
                  <span style={{ fontSize: '12px', color: '#10B981', fontWeight: 600 }}>+12%</span>
                </div>
                <h3 style={{
                  fontSize: isMobile ? '36px' : '42px',
                  fontWeight: 800,
                  color: COLORS.dark,
                  margin: '0 0 4px 0',
                  lineHeight: 1
                }}>
                  {toArabicNumerals(dashboardData.stats.hoursLearned.hours, isArabic)}{isArabic ? 'س' : 'h'} {toArabicNumerals(dashboardData.stats.hoursLearned.minutes, isArabic)}{isArabic ? 'د' : 'm'}
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: COLORS.textSecondary,
                  margin: 0
                }}>
                  {isArabic ? 'إجمالي الوقت' : 'Total Time'}
                </p>
                  </div>
              <div style={{
                width: '100%',
                height: '6px',
                backgroundColor: COLORS.gray,
                borderRadius: '3px',
                overflow: 'hidden',
                marginTop: '16px'
              }}>
                <div style={{
                  width: '65%',
                  height: '100%',
                  background: `linear-gradient(90deg, ${COLORS.orange}, ${COLORS.orangeLight})`,
                  borderRadius: '3px'
                }} />
                  </div>
                  </div>

            {/* Total Sessions Card */}
            <div style={{ 
              backgroundColor: COLORS.white,
              borderRadius: '16px',
              padding: isMobile ? '20px' : '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'space-between',
              position: 'relative',
              overflow: 'hidden',
              transition: 'transform 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
                <div style={{
                position: 'absolute',
                top: '-10px',
                right: '-10px',
                fontSize: '60px',
                opacity: 0.08
              }}>
                <FaBook size={48} style={{ color: '#9CA3AF', opacity: 0.08 }} />
                </div>
              <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
                  gap: '8px',
                  marginBottom: '16px'
        }}>
                  <span style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>
                    {isArabic ? 'إجمالي الجلسات' : 'Total Sessions'}
                  </span>
          <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '4px',
                    backgroundColor: '#FFE5D0',
                display: 'flex',
                alignItems: 'center',
                    justifyContent: 'center'
              }}>
                    <span style={{ fontSize: '10px' }}>🔥</span>
              </div>
            </div>
                <h3 style={{
                  fontSize: isMobile ? '42px' : '48px',
              fontWeight: 800,
                  color: COLORS.dark,
                  margin: '0 0 4px 0',
                  lineHeight: 1
                }}>
                  {toArabicNumerals(dashboardData.stats.totalSessions, isArabic)}
                </h3>
            <p style={{
                  fontSize: '14px',
                  color: COLORS.textSecondary,
                  margin: 0
                }}>
                  {isArabic ? 'مكتمل' : 'Completed'}
                </p>
              </div>
            </div>
          </div>

          {/* Upcoming Session Card */}
          {dashboardData.upcomingSession ? (
              <div style={{
              background: `linear-gradient(135deg, ${COLORS.blue} 0%, ${COLORS.dark} 100%)`,
              borderRadius: '16px',
              padding: isMobile ? '20px' : '28px',
              color: COLORS.white,
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: isMobile ? 'flex-start' : 'center',
              boxShadow: '0 8px 24px rgba(26, 35, 65, 0.2)',
              position: 'relative',
              overflow: 'hidden',
              gap: isMobile ? '20px' : '0'
            }}>
              <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '200px',
                height: '200px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)'
              }} />

              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '6px 12px',
                  borderRadius: '20px',
                  backgroundColor: 'rgba(255, 122, 26, 0.2)',
                  border: `1px solid ${COLORS.orange}`,
                  marginBottom: '16px',
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  <div style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: COLORS.orange
                  }} />
                  {isArabic ? 'قادم' : 'UP NEXT'}
                </div>
                <h3 style={{
                  fontSize: isMobile ? '20px' : '24px',
                  fontWeight: 700,
                  margin: '0 0 8px 0'
                }}>
                  {translateSubject(dashboardData.upcomingSession.subject, isArabic) || (isArabic ? 'جلسة' : 'Session')}
                </h3>
                <p style={{
                  fontSize: '14px',
                  margin: '0 0 4px 0',
                  opacity: 0.9
                }}>
                  {dashboardData.upcomingSession.request_type === 'individual'
                    ? (isArabic ? 'جلسة فردية' : 'Individual Session')
                    : (isArabic ? 'جلسة جماعية' : 'Group Session')
                  }
                  {dashboardData.upcomingSession.num_students > 1 && ` • ${toArabicNumerals(dashboardData.upcomingSession.num_students, isArabic)} ${isArabic ? 'طلاب' : 'students'}`}
                </p>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                  gap: '16px',
                  marginTop: '16px',
                  flexWrap: 'wrap'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      overflow: 'hidden',
                      border: '2px solid rgba(255,255,255,0.3)'
                    }}>
                      <img
                        src={dashboardData.upcomingSession.teacher_profile_image || DEFAULT_AVATAR}
                        alt="Teacher"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </div>
                    <span style={{ fontSize: '14px', fontWeight: 600 }}>
                      {dashboardData.upcomingSession.teacher_name || 'Teacher'}
                      </span>
                    </div>
                  <div style={{
                    fontSize: '13px',
                    opacity: 0.9,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <FaClock style={{ fontSize: '12px' }} />
                    {toArabicNumerals(dashboardData.upcomingSession.time_from, isArabic)} - {toArabicNumerals(dashboardData.upcomingSession.time_to, isArabic)}
                </div>
              </div>
              </div>

            <div style={{
              display: 'flex',
                gap: '12px',
                position: 'relative',
                zIndex: 1,
                width: isMobile ? '100%' : 'auto'
            }}>
              <button
                  onClick={() => navigate('/dashboard/student/chat')}
                style={{
                    padding: '14px 24px',
                    borderRadius: '12px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    backgroundColor: 'transparent',
                    color: COLORS.white,
                  fontWeight: 700,
                    fontSize: '14px',
                  cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    flex: isMobile ? 1 : 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'rgba(255,255,255,0.1)';
                    e.target.style.borderColor = 'rgba(255,255,255,0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.borderColor = 'rgba(255,255,255,0.3)';
                  }}
                >
                  ⋯
              </button>
              {dashboardData.upcomingSession.lesson_format === 'in-person' ? (
                  // Face-to-face session - show location info (clickable if coordinates available)
                  dashboardData.upcomingSession.location_latitude && dashboardData.upcomingSession.location_longitude ? (
                    <a
                      href={createMapsUrl(dashboardData.upcomingSession.location_latitude, dashboardData.upcomingSession.location_longitude)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        padding: '14px 28px',
                        borderRadius: '12px',
                        border: 'none',
                        backgroundColor: COLORS.orange,
                        color: COLORS.white,
                        fontWeight: 700,
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        boxShadow: '0 4px 12px rgba(255, 122, 26, 0.3)',
                        flex: isMobile ? 2 : 'none',
                        maxWidth: isMobile ? '100%' : '250px',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 16px rgba(255, 122, 26, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 12px rgba(255, 122, 26, 0.3)';
                      }}
                    >
                      <FaMapMarkerAlt size={16} style={{ color: '#FF7A1A' }} />
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                        {dashboardData.upcomingSession.location_formatted_address || dashboardData.upcomingSession.place || (isArabic ? 'عرض الموقع' : 'View Location')}
                      </div>
                      <span style={{ fontSize: '12px' }}>↗</span>
                    </a>
                  ) : (
                    <div
                      style={{
                        padding: '14px 28px',
                        borderRadius: '12px',
                        border: 'none',
                        backgroundColor: COLORS.orange,
                        color: COLORS.white,
                        fontWeight: 700,
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        boxShadow: '0 4px 12px rgba(255, 122, 26, 0.3)',
                        flex: isMobile ? 2 : 'none',
                        maxWidth: isMobile ? '100%' : '250px'
                      }}
                    >
                      <FaMapMarkerAlt size={16} style={{ color: '#FF7A1A' }} />
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {dashboardData.upcomingSession.place || (isArabic ? 'موقع وجهاً لوجه' : 'Face-to-face location')}
                      </div>
                    </div>
                  )
                ) : (
                  // Online session - show Join Now button
                  <button
                    onClick={() => {
                      if (dashboardData.upcomingSession.meeting_link) {
                        window.open(dashboardData.upcomingSession.meeting_link, '_blank');
                      } else {
                        navigate('/dashboard/student/teachers');
                      }
                    }}
                    style={{
                      padding: '14px 28px',
                      borderRadius: '12px',
                      border: 'none',
                      backgroundColor: COLORS.orange,
                      color: COLORS.white,
                      fontWeight: 700,
                      fontSize: '14px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 4px 12px rgba(255, 122, 26, 0.3)',
                      flex: isMobile ? 2 : 'none'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 16px rgba(255, 122, 26, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 12px rgba(255, 122, 26, 0.3)';
                    }}
                  >
                    <span>▶</span>
                    {isArabic ? 'انضم الآن' : 'Join Now'}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div style={{
              background: `linear-gradient(135deg, ${COLORS.blue} 0%, ${COLORS.dark} 100%)`,
              borderRadius: '16px',
              padding: '40px',
              textAlign: 'center',
              color: COLORS.white,
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Gradient overlay */}
              <div style={{
                    position: 'absolute',
                inset: 0,
                background: 'linear-gradient(to left, transparent, rgba(0, 0, 0, 0.1))',
                pointerEvents: 'none'
              }} />

              <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <FaBook size={48} style={{ color: '#9CA3AF', marginBottom: '16px' }} />
                <h3 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '8px', margin: 0 }}>
                  {isArabic ? 'لا توجد جلسات قادمة' : 'No Upcoming Sessions'}
                </h3>
                <p style={{ fontSize: '14px', opacity: 0.9, marginBottom: '20px', margin: '8px 0 20px 0' }}>
                  {isArabic ? 'ابدأ رحلتك التعليمية بحجز أول جلسة لك' : 'Start your learning journey by booking your first session'}
                </p>
                    <button
                  onClick={() => navigate('/dashboard/student/teachers')}
                      style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: '2px solid rgba(255,255,255,0.5)',
                    borderRadius: '12px',
                    padding: '12px 32px',
                    color: COLORS.white,
                fontWeight: 700, 
                  cursor: 'pointer',
                    fontSize: '14px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'rgba(255,255,255,0.3)';
                    e.target.style.borderColor = 'rgba(255,255,255,0.8)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'rgba(255,255,255,0.2)';
                    e.target.style.borderColor = 'rgba(255,255,255,0.5)';
                  }}
                >
                  {isArabic ? 'تصفح المدرسين' : 'Browse Tutors'} →
                </button>
                </div>
                </div>
              )}

          {/* Recommended Tutors */}
          <div style={{
            backgroundColor: COLORS.white,
            borderRadius: '16px',
            padding: isMobile ? '20px' : '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}>
            <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 700,
                color: COLORS.dark,
                margin: 0
              }}>
                {isArabic ? 'المعلمون الموصى بهم' : 'Recommended Tutors'}
              </h3>
                    <button
                onClick={() => navigate('/dashboard/student/teachers')}
                      style={{
                  color: COLORS.orange,
                  fontSize: '14px',
                    fontWeight: 600,
                  border: 'none', 
                  background: 'none',
                  cursor: 'pointer'
                }}
              >
                {isArabic ? 'عرض الكل' : 'View all'}
              </button>
            </div>

            {dashboardData.recommendedTeachers.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {dashboardData.recommendedTeachers.map((tutor, index) => (
                  <div
                    key={tutor.email || `tutor-${index}`}
                style={{
                  display: 'flex',
                      justifyContent: 'space-between',
                  alignItems: isMobile ? 'flex-start' : 'center',
                      padding: isMobile ? '12px' : '16px',
                      borderRadius: '12px',
                      border: `1px solid ${COLORS.borderLight}`,
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      flexDirection: isMobile ? 'column' : 'row',
                      gap: isMobile ? '12px' : '0'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = COLORS.orange;
                      e.currentTarget.style.backgroundColor = '#FFF8F1';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = COLORS.borderLight;
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    onClick={() => navigate('/dashboard/student/teachers')}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '16px', flex: 1, width: '100%' }}>
                    <div style={{ 
                        width: isMobile ? '40px' : '48px',
                        height: isMobile ? '40px' : '48px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        background: `linear-gradient(135deg, ${COLORS.orange}, ${COLORS.orangeLight})`,
                        flexShrink: 0
                      }}>
                        <img
                          src={tutor.profile_photo || DEFAULT_AVATAR}
                          alt={tutor.full_name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h4 style={{
                          fontSize: isMobile ? '14px' : '16px',
                          fontWeight: 600,
                          color: COLORS.dark,
                          margin: '0 0 4px 0',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {tutor.full_name}
                        </h4>
                        <p style={{
                          fontSize: isMobile ? '12px' : '14px',
                          color: COLORS.textSecondary,
                          margin: '0 0 4px 0',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {translateSubject(tutor.subjects?.[0], isArabic) || translateSubject('Mathematics', isArabic)}
                        </p>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '4px',
                    flexWrap: isMobile ? 'wrap' : 'nowrap',
                    minWidth: 0
                  }}>
                    <FaStar size={isMobile ? 10 : 12} style={{ color: '#FFA500', flexShrink: 0 }} />
                    {tutor.rating?.ratings_count > 0 ? (
                      <>
                        <span style={{ 
                          fontSize: isMobile ? '11px' : '12px', 
                          fontWeight: 600, 
                          color: COLORS.dark,
                          whiteSpace: 'nowrap'
                        }}>
                          {tutor.rating.avg_rating.toFixed(1)}
                        </span>
                        <span style={{ 
                          fontSize: isMobile ? '11px' : '12px', 
                          color: COLORS.textSecondary,
                          whiteSpace: isMobile ? 'normal' : 'nowrap'
                        }}>
                          ({tutor.rating.ratings_count} {isArabic ? 'تقييم' : 'reviews'})
                        </span>
                      </>
                    ) : (
                      <span style={{ 
                        fontSize: isMobile ? '11px' : '12px', 
                        color: COLORS.textSecondary,
                        whiteSpace: isMobile ? 'normal' : 'nowrap'
                      }}>
                        {isArabic ? 'لا توجد تقييمات' : 'No ratings yet'}
                      </span>
                    )}
                  </div>
                        </div>
                    </div>
                    <div style={{ 
                      textAlign: isMobile ? 'left' : 'right', 
                      marginLeft: isMobile ? '0' : '16px', 
                      flexShrink: 0,
                      width: isMobile ? '100%' : 'auto',
                      display: 'flex',
                      flexDirection: isMobile ? 'row' : 'column',
                      justifyContent: isMobile ? 'space-between' : 'flex-end',
                      alignItems: isMobile ? 'center' : 'flex-end',
                      gap: isMobile ? '8px' : '0'
                    }}>
                      <p style={{
                        fontSize: isMobile ? '16px' : '18px',
                        fontWeight: 700,
                        color: COLORS.dark,
                        margin: '0 0 8px 0'
                      }}>
                        {tutor.hourly_rate || '45'} BD<span style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 400 }}>/hr</span>
                      </p>
                      <div style={{
                        padding: isMobile ? '6px 10px' : '6px 12px',
                        borderRadius: '8px',
                        backgroundColor: index === 0 ? '#10B981' : COLORS.orange,
                        color: COLORS.white,
                        fontSize: isMobile ? '10px' : '11px',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        marginBottom: isMobile ? '0' : '0'
                      }}>
                        {index === 0 ? (isArabic ? 'متاح اليوم' : 'Available Today') : (isArabic ? 'احجز للغد' : 'Book for Tomorrow')}
                      </div>
                    </div>
                      </div>
                    ))}
                  </div>
            ) : (
                          <div style={{
                background: COLORS.gray,
                borderRadius: '12px',
                padding: '32px',
                textAlign: 'center'
              }}>
                {/* Show different message based on onboarding status */}
                {!dashboardData.onboardingCompleted ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <FaFileAlt size={40} style={{ color: '#9CA3AF', marginBottom: '12px' }} />
                    <h4 style={{ fontSize: '16px', fontWeight: 600, color: COLORS.dark, marginBottom: '8px', margin: '0 0 8px 0' }}>
                      {isArabic ? 'أكمل ملفك الشخصي للحصول على توصيات أفضل' : 'Complete Your Profile for Better Results'}
                    </h4>
                    <p style={{ fontSize: '13px', color: COLORS.textSecondary, marginBottom: '16px', margin: '0 0 16px 0' }}>
                        {isArabic 
                        ? 'املأ استمارة التفضيلات لنتمكن من توصيتك بأفضل المدرسين المناسبين لأهدافك التعليمية'
                        : 'Complete the preferences form so we can recommend the best tutors matching your learning goals'}
                    </p>
                            <button
                      onClick={() => navigate('/student-onboarding')}
                              style={{
                        background: COLORS.orange,
                        color: COLORS.white,
                        border: 'none',
                        borderRadius: '10px',
                        padding: '10px 24px',
                                fontWeight: 600,
                        fontSize: '13px',
                        cursor: 'pointer'
                              }}
                            >
                      {isArabic ? 'أكمل الاستمارة' : 'Complete Form'}
                            </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <FaSearch size={40} style={{ color: '#9CA3AF', marginBottom: '12px' }} />
                    <h4 style={{ fontSize: '16px', fontWeight: 600, color: COLORS.dark, marginBottom: '8px', margin: '0 0 8px 0' }}>
                      {isArabic ? 'لا توجد توصيات حالياً' : 'No Recommendations Yet'}
                    </h4>
                    <p style={{ fontSize: '13px', color: COLORS.textSecondary, marginBottom: '16px', margin: '0 0 16px 0' }}>
                      {isArabic
                        ? 'لم نتمكن من العثور على مدرسين يطابقون تفضيلاتك حالياً. جرب تصفح جميع المدرسين'
                        : 'We could not find tutors matching your preferences right now. Try browsing all tutors'}
                    </p>
                                <button
                      onClick={() => navigate('/dashboard/student/teachers')}
                                  style={{
                        background: COLORS.orange,
                        color: COLORS.white,
                        border: 'none',
                        borderRadius: '10px',
                        padding: '10px 24px',
                                    fontWeight: 600,
                        fontSize: '13px',
                        cursor: 'pointer'
                      }}
                    >
                       {isArabic ? 'استكشف المدرسين' : 'Explore Tutors'}
                     </button>
                   </div>
                 )}
                        </div>
                      )}
                          </div>
                        </div>
                      
        {/* Right Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '16px' : '24px' }}>
          {/* Calendar - Week View */}
                        <div style={{
            backgroundColor: COLORS.white,
            borderRadius: '16px',
            padding: isMobile ? '20px' : '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}>
            {/* Week title */}
            <h3 style={{
              fontSize: isMobile ? '16px' : '18px',
                            fontWeight: 700,
              color: COLORS.dark,
              margin: '0 0 20px 0'
            }}>
              {formatMonthYear(currentMonth, isArabic)}
            </h3>

            {/* Day headers - S M T W T F S */}
                    <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: isMobile ? '6px' : '8px',
              marginBottom: '8px'
            }}>
              {(isArabic ? ['ح', 'ن', 'ث', 'ر', 'خ', 'ج', 'س'] : ['S', 'M', 'T', 'W', 'T', 'F', 'S']).map((day, index) => (
                <div
                  key={index}
                  style={{
                    textAlign: 'center',
                    fontSize: isMobile ? '11px' : '12px',
                    fontWeight: 600,
                    color: COLORS.textSecondary,
                    padding: '4px',
                    letterSpacing: '0.5px'
                  }}
                >
                  {day}
                </div>
              ))}
                    </div>

            {/* Week Calendar Grid */}
            {renderCalendar()}

            {/* Selected Date Sessions */}
            {selectedDate && (
                    <div style={{
                marginTop: '20px',
                padding: '16px',
                backgroundColor: '#fff9f0',
                borderRadius: '12px',
                border: `2px solid ${COLORS.orange}`
              }}>
                    <div style={{
                  fontSize: '14px',
                  fontWeight: 700,
                  color: COLORS.orange,
                  marginBottom: '16px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {formatFullDate(new Date(selectedDate), isArabic)}
                </div>

                {/* Loading indicator */}
                {loadingSessions && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '20px',
                    color: COLORS.textSecondary,
                    fontSize: '14px'
                  }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      border: `3px solid ${COLORS.borderLight}`,
                      borderTop: `3px solid ${COLORS.orange}`,
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      marginRight: '12px'
                    }} />
                    {isArabic ? 'جاري التحميل...' : 'Loading sessions...'}
                      </div>
                )}

                {/* No confirmed sessions message */}
                {!loadingSessions && selectedDateSessions.length === 0 && (
                  <div style={{
                    textAlign: 'center',
                    padding: '20px',
                    color: COLORS.textSecondary,
                    fontSize: '14px'
                  }}>
                    {isArabic ? 'لا توجد جلسات مؤكدة في هذا اليوم' : 'No confirmed sessions on this day'}
                  </div>
                )}

                {/* Sessions list */}
                {!loadingSessions && selectedDateSessions.map((session, index) => (
                  <div
                    key={session.id || `session-${index}`}
                        style={{
                          display: 'flex',
                      gap: '12px',
                      padding: '12px',
                      backgroundColor: COLORS.white,
                      borderRadius: '8px',
                      marginBottom: index < selectedDateSessions.length - 1 ? '12px' : '0',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}
                  >
                    {/* Time */}
                    <div style={{
                      fontSize: '14px',
                      fontWeight: 700,
                      color: COLORS.dark,
                      minWidth: '50px'
                    }}>
                      {toArabicNumerals(session.time_from, isArabic)}
                    </div>

                    {/* Session Details */}
                    <div style={{ flex: 1 }}>
                    <div style={{
                        fontSize: '15px',
                      fontWeight: 600,
                        color: COLORS.dark,
                        marginBottom: '4px'
                      }}>
                        {translateSubject(session.subject, isArabic) || (isArabic ? 'جلسة تعليمية' : 'Tutoring Session')}
                    </div>
                    <div style={{
                        fontSize: '13px',
                        color: COLORS.textSecondary,
                        marginBottom: '6px'
                      }}>
                        {isArabic ? 'مع' : 'with'} {session.teacher_name}
                    </div>
                      <div style={{
                        display: 'inline-block',
                        fontSize: '11px',
                          fontWeight: 600,
                        color: session.lesson_format === 'online' ? '#10b981' : '#3b82f6',
                        backgroundColor: session.lesson_format === 'online' ? '#d1fae5' : '#dbeafe',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        textTransform: 'uppercase'
                      }}>
                        {session.lesson_format === 'online' ? (
                          <>
                            <FaGlobe size={14} style={{ marginRight: 4 }} />
                            {isArabic ? 'عبر الإنترنت' : 'Online'}
                          </>
                        ) : (
                          <>
                            <FaUser size={14} style={{ marginRight: 4 }} />
                            {isArabic ? 'وجهاً لوجه' : 'Face to Face'}
                          </>
                        )}
                        </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Today's Sessions - Clean design like reference */}
            {!selectedDate && dashboardData.todaySessions.length > 0 && (
              <div style={{
                marginTop: '24px',
                paddingTop: '20px',
                borderTop: `1px solid ${COLORS.borderLight}`
              }}>
                {dashboardData.todaySessions.map((session, index) => (
                  <div
                    key={session.id || `session-${index}`}
                            style={{
                              display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      marginBottom: index < dashboardData.todaySessions.length - 1 ? '16px' : '0'
                    }}
                  >
                    <FaClock style={{
                      fontSize: '14px',
                      color: COLORS.textSecondary,
                      marginTop: '2px',
                      flexShrink: 0
                    }} />
                            <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: 700,
                        color: COLORS.dark,
                        marginBottom: '2px'
                      }}>
                        {toArabicNumerals(session.time_from, isArabic)}
                              </div>
                      <div style={{
                        fontSize: '13px',
                        color: COLORS.textSecondary
                      }}>
                        {isArabic ? 'مع' : 'with'} {session.teacher_name}
                              </div>
                            </div>
                  </div>
                        ))}
                      </div>
                    )}
          </div>

          {/* Messages */}
                          <div style={{
            backgroundColor: COLORS.white,
            borderRadius: '16px',
            padding: isMobile ? '20px' : '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}>
                  <div style={{
                            display: 'flex',
              justifyContent: 'space-between',
                            alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 700,
                color: COLORS.dark,
                margin: 0
              }}>
                {isArabic ? 'الرسائل' : 'Messages'}
              </h3>
                            <button
                onClick={() => navigate('/dashboard/student/chat')}
                              style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                                border: 'none',
                  backgroundColor: COLORS.gray,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s ease'
                              }}
                onMouseEnter={(e) => e.target.style.backgroundColor = COLORS.borderLight}
                onMouseLeave={(e) => e.target.style.backgroundColor = COLORS.gray}
                            >
                <FaEdit style={{ fontSize: '14px', color: COLORS.dark }} />
                            </button>
                </div>

            {dashboardData.recentMessages.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {dashboardData.recentMessages.map((message, index) => {
                  const timeAgo = (() => {
                    const diff = Date.now() - new Date(message.created_at).getTime();
                    const minutes = Math.floor(diff / 60000);
                    const hours = Math.floor(diff / 3600000);
                    const days = Math.floor(diff / 86400000);

                    if (minutes < 60) return `${minutes}m ago`;
                    if (hours < 24) return `${hours}h ago`;
                    return `${days}d ago`;
                  })();

  return (
    <div
                      key={message.id || `message-${index}`}
      style={{
        display: 'flex',
        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        borderRadius: '12px',
        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = COLORS.gray}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      onClick={() => navigate('/dashboard/student/chat')}
                    >
        <div style={{
                        width: '40px',
                        height: '40px',
          borderRadius: '50%',
                        overflow: 'hidden',
                        position: 'relative',
                        flexShrink: 0
        }}>
          <img 
                          src={message.teacher_profile_image || DEFAULT_AVATAR}
                          alt={message.teacher_name || 'Teacher'}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        {index === 0 && (
                          <div style={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            backgroundColor: '#10B981',
                            border: `2px solid ${COLORS.white}`
                          }} />
                        )}
        </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ 
        display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '4px'
                        }}>
                          <h4 style={{
                            fontSize: '14px',
              fontWeight: 600,
                            color: COLORS.dark,
                            margin: 0,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {message.teacher_name || 'Teacher'}
                          </h4>
                          <span style={{
                            fontSize: '12px',
                            color: COLORS.textSecondary,
                            flexShrink: 0,
                            marginLeft: '8px'
                          }}>
                            {timeAgo}
            </span>
        </div>
                        <p style={{
                          fontSize: '13px',
                          color: COLORS.textSecondary,
                          margin: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
      }}>
                          {message.content || "No message content"}
                        </p>
        </div>
                    </div>
                  );
                })}
              </div>
            ) : (
      <div style={{
                background: COLORS.white,
                borderRadius: '12px',
                padding: '24px',
                textAlign: 'center',
                border: `1px solid ${COLORS.borderLight}`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <FaComments size={36} style={{ color: '#9CA3AF', marginBottom: '12px' }} />
                <p style={{ fontSize: '14px', color: COLORS.textSecondary, marginBottom: 0, margin: 0 }}>
                  {isArabic ? 'لا توجد رسائل حديثة' : 'No recent messages'}
                </p>
                <p style={{ fontSize: '12px', color: COLORS.textSecondary, marginTop: '4px', margin: '4px 0 0 0' }}>
                  {isArabic ? 'احجز جلسة لبدء المحادثة مع المدرسين' : 'Book a session to start chatting with tutors'}
                </p>
        </div>
            )}

          <button
              onClick={() => navigate('/dashboard/student/chat')}
            style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
              border: 'none',
                backgroundColor: COLORS.gray,
                color: COLORS.dark,
                fontSize: '14px',
                fontWeight: 600,
              cursor: 'pointer',
                marginTop: '12px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = COLORS.orange;
                e.target.style.color = COLORS.white;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = COLORS.gray;
                e.target.style.color = COLORS.dark;
              }}
            >
              {isArabic ? 'عرض جميع الرسائل' : 'View all messages'}
          </button>
          </div>
        </div>
      </div>

      {/* Find a Tutor Floating Button */}
          <button
        onClick={() => navigate('/dashboard/student/teachers')}
            style={{
          position: 'fixed',
          bottom: '32px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: isMobile ? '14px 24px' : '16px 32px',
          borderRadius: '50px',
              border: 'none',
          background: `linear-gradient(135deg, ${COLORS.orange} 0%, ${COLORS.orangeLight} 100%)`,
          color: COLORS.white,
          fontSize: isMobile ? '14px' : '16px',
          fontWeight: 700,
              cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
          gap: '12px',
          boxShadow: '0 8px 24px rgba(255, 122, 26, 0.3)',
            transition: 'all 0.3s ease',
          zIndex: 100
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = 'translateX(-50%) translateY(-4px)';
          e.target.style.boxShadow = '0 12px 32px rgba(255, 122, 26, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'translateX(-50%) translateY(0)';
          e.target.style.boxShadow = '0 8px 24px rgba(255, 122, 26, 0.3)';
        }}
      >
        <FaSearch />
        {isArabic ? 'ابحث عن مدرس' : 'Find a Tutor'}
          </button>
    </div>
  );
}
